import { useState, useCallback, ReactNode } from 'react';
import type { ConfirmationVariant } from '../components/ui/ConfirmationModal';

export interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string | ReactNode;
  confirmText: string;
  cancelText: string;
  variant: ConfirmationVariant;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export interface ConfirmOptions {
  title: string;
  message: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmationVariant;
}

/**
 * Hook pour gérer les confirmations de manière déclarative
 * Remplace les confirm() natifs par une modale accessible
 *
 * @example
 * ```tsx
 * const { confirm, confirmationProps } = useConfirmation();
 *
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: 'Supprimer cet élément ?',
 *     message: 'Cette action est irréversible.',
 *     variant: 'danger',
 *     confirmText: 'Supprimer'
 *   });
 *
 *   if (confirmed) {
 *     // Effectuer la suppression
 *   }
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleDelete}>Supprimer</button>
 *     <ConfirmationModal {...confirmationProps} />
 *   </>
 * );
 * ```
 */
export function useConfirmation() {
  const [state, setState] = useState<ConfirmationState>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    variant: 'info',
    isLoading: false,
    onConfirm: () => {},
    onCancel: () => {}
  });

  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setResolveRef(() => resolve);
      setState({
        isOpen: true,
        title: options.title,
        message: options.message,
        confirmText: options.confirmText || 'Confirmer',
        cancelText: options.cancelText || 'Annuler',
        variant: options.variant || 'info',
        isLoading: false,
        onConfirm: () => {
          setState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }));
  }, []);

  const close = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
    if (resolveRef) {
      resolveRef(false);
    }
  }, [resolveRef]);

  // Props à passer à ConfirmationModal
  const confirmationProps = {
    isOpen: state.isOpen,
    title: state.title,
    message: state.message,
    confirmText: state.confirmText,
    cancelText: state.cancelText,
    variant: state.variant,
    isLoading: state.isLoading,
    onConfirm: state.onConfirm,
    onCancel: state.onCancel
  };

  return {
    confirm,
    confirmationProps,
    setLoading,
    close,
    isOpen: state.isOpen
  };
}

export default useConfirmation;
